/**
 * Timezone Context for Digital Ink AI
 * Detects user's timezone on mount and provides it app-wide
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

const TimezoneContext = createContext();

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within TimezoneProvider');
  }
  return context;
};

export const TimezoneProvider = ({ children }) => {
  const [userTimezone, setUserTimezone] = useState('America/Denver'); // Default MST
  const [timezoneOffset, setTimezoneOffset] = useState(-7); // Default MST offset
  const [timezoneAbbr, setTimezoneAbbr] = useState('MST');
  const [isDetected, setIsDetected] = useState(false);

  useEffect(() => {
    detectTimezone();
  }, []);

  const detectTimezone = () => {
    try {
      // CRITICAL FIX: Browser timezone detection in cloud environments returns server timezone (UTC)
      // Instead, we use America/Denver as the default for Digital Ink AI's primary market
      // Users can override this in their profile settings if needed
      
      const tz = 'America/Denver'; // Primary market timezone
      
      // Calculate actual offset for Denver
      const denverTime = new Date().toLocaleString('en-US', { timeZone: 'America/Denver' });
      const denverDate = new Date(denverTime);
      const utcDate = new Date();
      const offsetMinutes = (denverDate - utcDate) / (1000 * 60);
      const offsetHours = Math.round(offsetMinutes / 60);
      
      
      // Get timezone abbreviation
      const abbr = getTimezoneAbbr(tz);
      
      setUserTimezone(tz);
      setTimezoneOffset(offsetHours);
      setTimezoneAbbr(abbr);
      setIsDetected(true);
      
      // Store in localStorage for persistence
      localStorage.setItem('user_timezone', tz);
      localStorage.setItem('timezone_offset', offsetHours.toString());
      
    } catch (error) {
      console.error('❌ Timezone detection failed:', error);
      // Keep defaults (MST)
      setIsDetected(true);
    }
  };

  const getTimezoneAbbr = (tz) => {
    try {
      const date = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(date);
      const tzPart = parts.find(part => part.type === 'timeZoneName');
      return tzPart ? tzPart.value : 'Local';
    } catch (error) {
      return 'Local';
    }
  };

  /**
   * Convert UTC timestamp to user's local time and format it
   * @param {string|Date} utcTimestamp - UTC timestamp from backend
   * @param {Object} options - Formatting options
   * @returns {string} Formatted timestamp in user's timezone
   */
  const formatTimestamp = (utcTimestamp, options = {}) => {
    if (!utcTimestamp) return 'N/A';
    
    try {
      const date = new Date(utcTimestamp);
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', utcTimestamp);
        return 'Invalid Date';
      }
      
      const {
        includeTime = true,
        includeSeconds = false,
        includeTimezone = false,
        dateStyle = 'medium', // 'short', 'medium', 'long', 'full'
        timeStyle = 'short'   // 'short', 'medium', 'long'
      } = options;
      
      const formatOptions = {
        timeZone: userTimezone
      };
      
      if (includeTime) {
        formatOptions.year = 'numeric';
        formatOptions.month = 'short';
        formatOptions.day = 'numeric';
        formatOptions.hour = 'numeric';
        formatOptions.minute = '2-digit';
        if (includeSeconds) {
          formatOptions.second = '2-digit';
        }
        formatOptions.hour12 = true;
        
        if (includeTimezone) {
          formatOptions.timeZoneName = 'short';
        }
      } else {
        formatOptions.year = 'numeric';
        formatOptions.month = 'short';
        formatOptions.day = 'numeric';
      }
      
      return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    } catch (error) {
      console.error('Timestamp formatting error:', error, 'Input:', utcTimestamp);
      return 'Error';
    }
  };

  /**
   * Format date only (no time)
   */
  const formatDateOnly = (utcTimestamp) => {
    return formatTimestamp(utcTimestamp, { includeTime: false });
  };

  /**
   * Format with timezone abbreviation
   */
  const formatWithTimezone = (utcTimestamp) => {
    return formatTimestamp(utcTimestamp, { includeTime: true, includeTimezone: true });
  };

  const value = {
    userTimezone,
    timezoneOffset,
    timezoneAbbr,
    isDetected,
    formatTimestamp,
    formatDateOnly,
    formatWithTimezone,
    detectTimezone
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};
